import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Loader2, CheckCircle2, ArrowRight, Factory,
  Tractor, HardHat, Pickaxe, MonitorSmartphone, Truck, Stethoscope,
  Paperclip, X as XIcon, FileText,
} from "lucide-react";
import { Honeypot, useAntiSpam } from "@/components/site/Honeypot";
import { usePartner } from "@/context/PartnerContext";
import { useReferral } from "@/context/ReferralContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  validateRut,
  validateEmail,
  validateCelular,
  formatRut,
} from "@/lib/validation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Opciones del selector de industria (tarjetas con ícono). Multi-select.
const INDUSTRIAS = [
  { value: "agricola", label: "Agrícola", desc: "Tractores, cosechadoras, pulverizadoras, etc.", Icon: Tractor },
  { value: "construccion", label: "Construcción", desc: "Excavadoras, grúas, cargadores, etc.", Icon: HardHat },
  { value: "mineria", label: "Minería", desc: "Camiones mineros, palas, perforadoras, etc.", Icon: Pickaxe },
  { value: "electronicos", label: "Equipos electrónicos", desc: "PC, celulares, comunicaciones, medición, etc.", Icon: MonitorSmartphone },
  { value: "medicas", label: "Máquinas médicas", desc: "MRI, tomógrafos, ecógrafos, monitores, etc.", Icon: Stethoscope },
  { value: "vehiculos", label: "Vehículos", desc: "Camiones, camionetas, furgones, etc.", Icon: Truck },
];

const EMPTY = {
  nombre: "",
  rut: "",
  celular: "",
  email: "",
  tipo_industria: [],
};

const Field = ({ id, label, children, error }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-sm font-medium text-brand-ink">
      {label} <span className="text-red-500">*</span>
    </Label>
    {children}
    {error && (
      <p data-testid={`error-${id}`} className="text-xs font-medium text-red-600">
        {error}
      </p>
    )}
  </div>
);

const inputCls =
  "h-10 border-gray-300 bg-white focus-visible:ring-1 focus-visible:ring-brand-yellow focus-visible:border-brand-yellow";

const IndustriaPicker = ({ value, onChange, error }) => {
  const selected = Array.isArray(value) ? value : [];
  const toggle = (v) => {
    if (selected.includes(v)) onChange(selected.filter((x) => x !== v));
    else onChange([...selected, v]);
  };
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between">
        <Label className="text-sm font-medium text-brand-ink">
          Tipo(s) de industria <span className="text-red-500">*</span>
        </Label>
        <span className="text-[11px] text-gray-500">Puedes elegir más de una</span>
      </div>
      <div className="grid grid-cols-2 gap-2" data-testid="industria-picker">
        {INDUSTRIAS.map((opt, idx) => {
          const active = selected.includes(opt.value);
          const Icon = opt.Icon;
          const isLast = idx === INDUSTRIAS.length - 1;
          const oddCount = INDUSTRIAS.length % 2 === 1;
          return (
            <button
              type="button"
              key={opt.value}
              data-testid={`industria-option-${opt.value}`}
              onClick={() => toggle(opt.value)}
              className={`group relative flex items-start gap-2.5 rounded-2xl border-2 px-3 py-2.5 text-left transition-all ${
                isLast && oddCount ? "col-span-2" : ""
              } ${
                active
                  ? "border-brand-yellow bg-brand-yellow/10 shadow-sm"
                  : "border-gray-200 bg-white hover:border-brand-yellow/50 hover:bg-brand-yellow/5"
              }`}
              aria-pressed={active}
            >
              <span
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-colors ${
                  active ? "bg-brand-yellow text-brand-ink" : "bg-brand-ink/5 text-brand-ink group-hover:bg-brand-yellow/20"
                }`}
              >
                <Icon className="h-5 w-5" />
              </span>
              <span className="min-w-0 flex-1">
                <p className="text-sm font-semibold leading-tight text-brand-ink">
                  {opt.label}
                </p>
                <p className="mt-0.5 text-[11px] leading-snug text-gray-500 line-clamp-2">
                  {opt.desc}
                </p>
              </span>
              {active && (
                <CheckCircle2 className="absolute top-2 right-2 h-4 w-4 text-brand-yellow" />
              )}
            </button>
          );
        })}
      </div>
      {error && (
        <p data-testid="error-tipo_industria" className="text-xs font-medium text-red-600">
          {error}
        </p>
      )}
    </div>
  );
};

export const LeadForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [policyFile, setPolicyFile] = useState(null);
  const { honeypot, setHoneypot, getFields } = useAntiSpam();
  const { partner } = usePartner();
  const { ref } = useReferral();

  const update = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error("El archivo supera 10MB. Sube uno más liviano.");
      e.target.value = "";
      return;
    }
    const allowed = ["application/pdf", "image/png", "image/jpeg", "image/webp"];
    if (!allowed.includes(f.type)) {
      toast.error("Formato no permitido. Sube PDF, JPG, PNG o WEBP.");
      e.target.value = "";
      return;
    }
    setPolicyFile(f);
  };

  const validate = () => {
    const e = {};
    if (!form.nombre.trim()) e.nombre = "Ingresa tu nombre o razón social";
    if (!validateRut(form.rut)) e.rut = "RUT inválido (persona o empresa)";
    if (!validateCelular(form.celular)) e.celular = "Teléfono inválido";
    if (!validateEmail(form.email)) e.email = "Email inválido";
    if (!Array.isArray(form.tipo_industria) || form.tipo_industria.length === 0) {
      e.tipo_industria = "Selecciona al menos un tipo de industria";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Revisa los campos marcados.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/leads`, { ...form, ...getFields(), partner, ref });
      // Si hay póliza adjunta, subirla en un segundo paso
      if (policyFile && res?.data?.id) {
        try {
          const fd = new FormData();
          fd.append("file", policyFile);
          await axios.post(`${API}/leads/${res.data.id}/current-policy`, fd);
        } catch (upErr) {
          // No bloqueamos el envío por un fallo en el upload
          const detail = upErr?.response?.data?.detail;
          toast.warning(typeof detail === "string" ? `Póliza no adjuntada: ${detail}` : "No pudimos adjuntar tu póliza, pero recibimos tu solicitud.");
        }
      }
      setDone(true);
      toast.success("¡Solicitud enviada! Te contactaremos rápidamente.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No pudimos enviar tu solicitud. Intenta nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        data-testid="lead-success"
        className="rounded-3xl border border-gray-200 bg-white p-10 text-center shadow-xl shadow-brand-ink/5"
      >
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-brand-yellow/15 text-brand-yellow">
          <CheckCircle2 className="h-8 w-8" />
        </div>
        <h3 className="font-display mt-6 text-2xl font-bold text-brand-ink">
          ¡Gracias! Recibimos tu solicitud.
        </h3>
        <p className="mt-3 text-gray-600">
          Un ejecutivo se contactará contigo rápidamente con una propuesta para tu
          maquinaria o equipo.
        </p>
        <Button
          data-testid="new-quote-btn"
          variant="outline"
          onClick={() => { setForm(EMPTY); setDone(false); setPolicyFile(null); }}
          className="mt-6 rounded-full border-gray-300"
        >
          Enviar otra solicitud
        </Button>
      </motion.div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      data-testid="lead-form"
      className="rounded-3xl bg-white/80 backdrop-blur-md ring-1 ring-white/60 p-4 sm:p-5 shadow-xl shadow-brand-ink/10"
    >
      <Honeypot value={honeypot} onChange={setHoneypot} />

      <div className="flex items-start gap-2 text-brand-ink">
        <Factory className="h-5 w-5 text-brand-yellow shrink-0 mt-0.5" />
        <div className="min-w-0">
          <h3 className="font-display text-lg sm:text-xl font-bold leading-tight">
            Cotízanos tu seguro, completa y te contactamos.
          </h3>
          <p className="mt-1 text-xs sm:text-sm text-gray-600">
            Tenemos la <span className="font-semibold text-brand-ink">mejor cobertura y precio</span> del mercado.
          </p>
        </div>
      </div>

      <div className="mt-3 grid gap-3">
        <IndustriaPicker
          value={form.tipo_industria}
          onChange={(v) => update("tipo_industria", v)}
          error={errors.tipo_industria}
        />
        <div className="grid sm:grid-cols-2 gap-3">
          <Field id="nombre" label="Nombre o razón social" error={errors.nombre}>
            <Input
              id="nombre"
              data-testid="personal-nombre-input"
              value={form.nombre}
              onChange={(e) => update("nombre", e.target.value)}
              placeholder="Juan Pérez / Constructora Los Andes SpA"
              className={inputCls}
            />
          </Field>
          <Field id="email" label="Email de contacto" error={errors.email}>
            <Input
              id="email"
              data-testid="personal-email-input"
              type="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              placeholder="tucorreo@email.com"
              className={inputCls}
            />
          </Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field id="rut" label="RUT (persona o empresa)" error={errors.rut}>
            <Input
              id="rut"
              data-testid="personal-rut-input"
              value={form.rut}
              onChange={(e) => update("rut", formatRut(e.target.value))}
              placeholder="12.345.678-5 / 76.543.210-K"
              className={inputCls}
              maxLength={12}
            />
          </Field>
          <Field id="celular" label="Teléfono de contacto" error={errors.celular}>
            <Input
              id="celular"
              data-testid="personal-celular-input"
              value={form.celular}
              onChange={(e) => update("celular", e.target.value)}
              placeholder="+56 9 1234 5678"
              className={inputCls}
            />
          </Field>
        </div>

        {/* Póliza actual (opcional) — compacto */}
        <div className="rounded-xl border border-dashed border-brand-yellow/60 bg-brand-yellow/5 p-2.5" data-testid="policy-upload-block">
          {!policyFile ? (
            <label
              htmlFor="current-policy-file"
              className="flex cursor-pointer items-center gap-2.5 text-left"
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-yellow/25 text-brand-ink">
                <Paperclip className="h-4 w-4" />
              </span>
              <span className="min-w-0 flex-1">
                <p className="text-[11px] font-bold uppercase tracking-wide text-brand-yellow">
                  Opcional · ¿Quieres mejorar tu seguro?
                </p>
                <p className="text-xs font-semibold text-brand-ink leading-snug">
                  Adjúntanos tu póliza actual{" "}
                  <span className="font-normal text-gray-500">— PDF o imagen (máx. 10MB)</span>
                </p>
              </span>
              <span className="shrink-0 rounded-full bg-brand-ink px-3 py-1 text-[11px] font-semibold text-white hover:bg-brand-inkHover transition-colors">
                Adjuntar
              </span>
              <input
                id="current-policy-file"
                data-testid="current-policy-file"
                type="file"
                accept="application/pdf,image/png,image/jpeg,image/webp"
                onChange={onFileChange}
                className="hidden"
              />
            </label>
          ) : (
            <div className="flex items-center gap-2.5">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
                <FileText className="h-4 w-4" />
              </span>
              <span className="min-w-0 flex-1">
                <p className="text-[11px] font-bold uppercase tracking-wide text-emerald-700">
                  Póliza lista para enviar
                </p>
                <p className="truncate text-xs font-semibold text-brand-ink" title={policyFile.name}>
                  {policyFile.name}{" "}
                  <span className="font-normal text-gray-500">
                    ({(policyFile.size / 1024).toFixed(0)} KB)
                  </span>
                </p>
              </span>
              <button
                type="button"
                data-testid="current-policy-remove"
                onClick={() => setPolicyFile(null)}
                className="shrink-0 rounded-full border border-gray-200 bg-white p-1 text-gray-500 hover:text-red-600 hover:border-red-300 transition-colors"
                aria-label="Quitar archivo"
              >
                <XIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>

      <Button
        type="submit"
        data-testid="submit-lead-button"
        disabled={loading}
        className="mt-5 h-11 w-full rounded-full bg-brand-ink hover:bg-brand-inkHover text-white text-base font-semibold transition-all hover:-translate-y-0.5 shadow-lg shadow-brand-ink/20"
      >
        {loading ? (
          <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Enviando...</>
        ) : (
          <>Quiero cotizar mi seguro <ArrowRight className="ml-1 h-4 w-4" /></>
        )}
      </Button>
      <p className="mt-2 text-center text-[11px] text-gray-500">
        Sin costo y sin compromiso. Tus datos están protegidos.
      </p>
    </form>
  );
};
